import { useCreatorDisplay } from "@/hooks/useCreatorDisplay";
import { CreatorData } from "@/types/creatorData";
import Image from "next/image";
import { useState } from "react";

interface ExpertProfileProps {
    creator?: CreatorData;
    editable?: boolean;
    builtByExpert?: {
        name: string;
        title: string;
        image?: string;
        points: string[];
    };
    onBuiltByExpertChange?: (data: {
        name: string;
        title: string;
        image?: string;
        points: string[];
    }) => void;
    onImageUpload?: (file: File) => Promise<string>;
}

export default function ExpertProfile({
    creator,
    editable = false,
    builtByExpert,
    onBuiltByExpertChange,
    onImageUpload
}: ExpertProfileProps) {
    const { creatorName: defaultCreatorName, creatorImage: defaultCreatorImage } = useCreatorDisplay(creator);
    const [uploadingImage, setUploadingImage] = useState(false);

    // Prioritize builtByExpert data if available, otherwise fall back to creator data
    // In both editable and non-editable modes, show builtByExpert data first
    const displayName = builtByExpert?.name || defaultCreatorName;
    // Service Provider | title (fallback labels when missing data)
    const displayTitle = builtByExpert?.title || (creator ? "ผู้ให้บริการ" : "ตำแหน่ง");
    const displayImage = builtByExpert?.image || defaultCreatorImage;

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !onImageUpload || !builtByExpert || !onBuiltByExpertChange) return;

        try {
            setUploadingImage(true);
            const imageUrl = await onImageUpload(file);
            onBuiltByExpertChange({
                ...builtByExpert,
                image: imageUrl,
            });
        } catch (error) {
            console.error("Failed to upload image:", error);
            // Failed to upload image. Please try again.
            alert("อัปโหลดรูปไม่สำเร็จ กรุณาลองอีกครั้ง");
        } finally {
            setUploadingImage(false);
        }
    };

    return (
        <div className="flex gap-4">
            <div className="w-40 h-40 bg-gray-200 rounded-lg shrink-0 flex flex-col gap-2 items-center justify-center relative">
                <div className="w-16 h-16 bg-gray-300 rounded-full overflow-hidden relative">
                    {displayImage ? (
                        <Image src={displayImage} alt={displayName} className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            {/* No image */}
                            <Image
                                src="/logo.png"
                                alt="Aera"
                                width={48}
                                height={48}
                                className="opacity-80"
                            />
                        </div>
                    )}
                </div>

                {editable && builtByExpert && onBuiltByExpertChange && (
                    <label className="cursor-pointer text-xs text-blue-600 hover:text-blue-800 underline">
                        {uploadingImage ? (
                            <>
                                {/* Uploading... */}
                                กำลังอัปโหลด...
                            </>
                        ) : (
                            <>
                                {/* Change photo */}
                                เปลี่ยนรูป
                            </>
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            disabled={uploadingImage}
                            className="hidden"
                        />
                    </label>
                )}

                <div className="text-center px-2 w-full">
                    {editable && builtByExpert && onBuiltByExpertChange ? (
                        <>
                            {/* Expert name */}
                            <input
                                type="text"
                                value={builtByExpert.name}
                                onChange={(e) => onBuiltByExpertChange({
                                    ...builtByExpert,
                                    name: e.target.value,
                                })}
                                onFocus={(e) => e.target.select()}
                                placeholder="ชื่อผู้เชี่ยวชาญ"
                                className="w-full font-semibold text-aera-charcoal placeholder:text-aera-gray text-sm text-center border-b border-gray-300 focus:border-gray-900 focus:outline-none bg-transparent mb-1"
                            />
                            {/* Title/Position */}
                            <input
                                type="text"
                                value={builtByExpert.title}
                                onChange={(e) => onBuiltByExpertChange({
                                    ...builtByExpert,
                                    title: e.target.value,
                                })}
                                onFocus={(e) => e.target.select()}
                                placeholder="ตำแหน่ง/บทบาท"
                                className="w-full text-xs text-aera-charcoal placeholder:text-aera-gray text-center border-b border-gray-300 focus:border-gray-900 focus:outline-none bg-transparent"
                            />
                        </>
                    ) : (
                        <>
                            <h3 className="font-semibold text-aera-slate text-sm line-clamp-1">{displayName}</h3>
                            <p className="text-xs text-aera-slate line-clamp-1">{displayTitle}</p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}